import type { FC } from 'react'
import React from 'react'
import { GetClientPublicationsQueryParamsType, useGetClientPublications, useGetMainPublication } from '@/api/kubik/infoportal'
import { ArticlePreviewBanner, JournalSearchInput, RubricsMenu, TagsMenu } from '@/components/composed'
import { Articles, NewsPreview } from '@/components/resolvers'
import { PaginationMoreButton } from '@/components/ui'
import { useInfiniteJournalPublications } from '@/utils/infoportal/hooks/useInfiniteJournalPublications'
import { useJournalTags } from '@/utils/infoportal/hooks/useJournalTags'
import { EJournalClickEventBlockId, JournalGtmClickEventPayload } from '../../gtmConstants'
import { openArticleClicked } from '../../gtmHelpers'
import { messages } from './messages'
import './styles.scss'

const JournalIndex: FC = () => {
  const {
    publications: articles,
    isPublicationsInitialLoading: isArticlesInitialLoading,
    isPublicationsLoading: isArticlesLoading,
    hasNextPage,
    getNextPage,
  } = useInfiniteJournalPublications({
    type: GetClientPublicationsQueryParamsType.ARTICLE,
  })

  const {
    data: newsData,
    isLoading: isNewsLoading,
    isSuccess: isNewsLoaded,
    isError: isNewsFailed,
  } = useGetClientPublications(
    { type: GetClientPublicationsQueryParamsType.NEWS, includeMain: true, limit: 5 },
    { query: { staleTime: Infinity } },
  )

  const { tags, isTagsLoading } = useJournalTags()
  const { data: mainPublication, isLoading: isMainPublicationLoading } = useGetMainPublication({ query: { staleTime: Infinity } })

  const { publications: news = [] } = newsData?.data || {}

  const shouldShowNews = (isNewsLoaded && !isNewsFailed) || news.length > 0

  const selectedForMainNews = news.filter(({ main }) => !main)

  return (
    <>
      <RubricsMenu />
      <JournalSearchInput />

      {shouldShowNews && (
        <div className="journal__header">
          <ArticlePreviewBanner
            article={mainPublication?.data}
            className="journal__mainPublication"
            isLoading={isMainPublicationLoading}
            onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
          />

          <NewsPreview
            news={selectedForMainNews}
            isLoading={isNewsLoading}
            onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
          >
            <TagsMenu isLoading={isTagsLoading} tags={tags} />
          </NewsPreview>
        </div>
      )}

      <div className="journal__articles page-wrapper">
        <h2 className="journal__articlesHeader">{messages.articlesHeader}</h2>

        <Articles
          articles={articles}
          isArticlesLoading={isArticlesInitialLoading}
          blockId={EJournalClickEventBlockId.listArticles}
          onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
        />

        <PaginationMoreButton
          className="journal__loadMoreBtn"
          onClick={getNextPage}
          isLoading={isArticlesLoading}
          hasNextPage={hasNextPage}
        />
      </div>
    </>
  )
}

export default JournalIndex
